// Enable only after server-side payment fulfilment has passed end-to-end checks.
export const PAID_CHECKOUT_ENABLED = false;

// Pricing Configuration for Tekyu
// Central source of truth for all pricing tiers and features

// Base URL for success redirects - update for production
export const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://tekyu.co.uk';
};

// Generate success URL with plan details
export const getSuccessUrl = (plan, period) => {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/checkout-success?plan=${plan}&period=${period}`;
};

// Build Stripe Payment Link with redirect URL
// NOTE: For this to work, you MUST configure the success_url in Stripe Dashboard
// Go to: Stripe Dashboard > Payment Links > [Your Link] > After payment > Redirect URL
// Set it to: https://devrri2g1g5ax.cloudfront.net/checkout-success
export const getStripePaymentUrl = (tier, period, email = null) => {
  if (!PAID_CHECKOUT_ENABLED) return null;
  const tierConfig = PRICING_TIERS[tier.toUpperCase()];
  if (!tierConfig?.stripeLinks) return null;

  const isYearly = period === 'yearly';
  let url = isYearly ? tierConfig.stripeLinks.yearly : tierConfig.stripeLinks.monthly;

  if (!url) return null;

  // Add email prefill if available
  if (email) {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}prefilled_email=${encodeURIComponent(email)}`;
  }

  return url;
};

export const PRICING_TIERS = {
  FREE: {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: [
      'Career roadmap generation',
      'Basic skill assessments',
      '1 starter project preview',
    ],
    projectAccess: 1,  // GitHub Portfolio project
    cta: 'Get Started Free',
    popular: false,
  },
  STARTER: {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 6,
    yearlyPrice: 65, // ~1 month free
    features: [
      'Everything in Free',
      'Full personalized roadmaps',
      '4 hands-on starter projects',
      'Progress tracking',
      'Certificate of completion',
    ],
    projectAccess: 4,
    cta: 'Start Learning',
    popular: false,
    stripeLinks: {
      monthly: 'https://buy.stripe.com/6oU5kEg2f1klbp335Igfu0k',
      yearly: 'https://buy.stripe.com/14A5kEcQ3aUVfFjdKmgfu0l',
    },
  },
  PRO: {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 12,
    yearlyPrice: 120, // 2 months free (saves £24)
    features: [
      'Everything in Starter',
      'All premium projects',
      'Certification revision & practice exams',
      'Guided project explanations',
      'CV generator',
    ],
    projectAccess: 'unlimited',
    cta: 'Go Pro',
    popular: true,
    stripeLinks: {
      monthly: 'https://buy.stripe.com/6oU4gA8zN0ghdxb21Egfu0o',
      yearly: 'https://buy.stripe.com/4gM00k17le77akZ6hUgfu0n',
    },
  },
};

// Project tier mapping - matches the project IDs in tekyuProjects.js
export const PROJECT_TIERS = {
  FREE: [
    'github-portfolio-builder',
  ],
  STARTER: [
    'devsecops-github-aws',
    'devsecops-gitlab-azure',
    'threat-modelling',
    'kubernetes-security',
  ],
  PRO: [
    // Pro-only DevSecAI projects
    'advanced-threat-hunting',
    'terraform-security',
    'snowflake-security',
    'databricks-security',
    'sagemaker-ml-security',
    'llm-security-owasp',
    'n8n-agent-security',
  ],
};

// Check if user has access to a specific project
export const hasProjectAccess = (projectId, userTier) => {
  if (userTier === 'pro') return true;
  if (userTier === 'starter') {
    return PROJECT_TIERS.STARTER.includes(projectId);
  }
  return false;
};

// Get the required tier for a project
export const getProjectTier = (projectId) => {
  if (PROJECT_TIERS.STARTER.includes(projectId)) return 'starter';
  if (PROJECT_TIERS.PRO.includes(projectId)) return 'pro';
  return 'free';
};

// Format price for display
export const formatPrice = (price, currency = '£') => {
  if (price === 0) return 'Free';
  return `${currency}${price}`;
};

// Calculate yearly savings
export const getYearlySavings = (tier) => {
  if (!PAID_CHECKOUT_ENABLED) return null;
  const tierConfig = PRICING_TIERS[tier.toUpperCase()];
  if (!tierConfig) return 0;
  const monthlyTotal = tierConfig.monthlyPrice * 12;
  return monthlyTotal - tierConfig.yearlyPrice;
};

export default PRICING_TIERS;
